import React, { useCallback, useEffect, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import Autocomplete from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Drawer from "veneer/Drawer";
import ListItem from "veneer/ListItem";
import { showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";
import Typography from "veneer/Typography";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  EDIT_USER,
  INVITE_USER,
} from "cortex/ui/src/redux-store/users/actions";
import ToggleSection from "cortex/ui/src/ui/toggle-section";
import { FormAction, ROLE, USER_ROLES, User } from "cortex/ui/src/user/model";

type Option = { detail?: string; label: string; value: string };

type Mode = "invite" | "edit";

type FormProps = {
  disabled: boolean | null;
  email: string;
  name: string;
  role: string;
  networks: ReadonlyArray<number> | null;
};

type ErrorState<T> = Partial<Record<keyof T, string>>;

interface UserFormModalProps {
  mode: Mode;
  networkOptions?: ReadonlyArray<Option>;
  onCancel: () => void;
  open: boolean;
  ssoEnabled?: boolean;
  user?: User;
}

export default function UserFormModal({
  mode,
  networkOptions = [],
  onCancel,
  open,
  ssoEnabled = false,
  user,
}: UserFormModalProps) {
  const dispatch = useAppDispatch();
  const userIsDisabled = user?.disabled ?? false;

  const [form, setForm] = useState<FormProps>({
    disabled: null,
    email: "",
    name: "",
    networks: null,
    role: "",
  });

  const [errors, setErrors] = useState<ErrorState<FormProps>>({});

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && user) {
      setForm({
        disabled: user.disabled,
        email: "",
        name: user.name,
        role: user.role,
        networks: user.networks ?? null,
      });
      setErrors({});
    } else if (mode === "invite") {
      setForm({
        disabled: false,
        email: "",
        name: "",
        role: "",
        networks: null,
      });
      setErrors({});
    }
  }, [mode, user, open]);

  const enableNetworkSelection = useMemo(() => {
    const { role } = form;
    return role === ROLE.OPERATOR || role === ROLE.READ_ONLY;
  }, [form.role]);

  const clearError = useCallback((key: keyof FormProps) => {
    setErrors((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  const validate = useCallback(
    (state: FormProps): ErrorState<FormProps> => {
      const next: ErrorState<FormProps> = {};
      if (mode === "invite" && !state.name.trim()) {
        next.name = "Name is required.";
      }
      if (!state.role) next.role = "Role is required.";
      if (mode === "invite") {
        const email = (state as FormProps).email?.trim();
        if (!email) next.email = "Email is required.";
        else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
          next.email = "Email is not a valid email address.";
        }
      }
      if (
        enableNetworkSelection &&
        (!state.networks || state.networks.length === 0)
      ) {
        next.networks = "At least one network must be selected.";
      }

      setErrors(next);
      return next;
    },
    [enableNetworkSelection, mode],
  );

  const handleCommit = useCallback(async () => {
    const validation = validate(form);
    if (Object.keys(validation).length > 0) return;
    try {
      if (mode === "invite") {
        const { disabled, name, role, networks: networkIds, email } = form;
        await createPromiseApiAction(dispatch, INVITE_USER, [
          name,
          email,
          role,
          networkIds,
          disabled,
        ]);

        const action = ssoEnabled ? "added" : "invited";
        showSuccess(`User ${action} successfully.`);
      } else {
        if (!user) return;
        const { disabled, role, networks: networkIds } = form;
        await createPromiseApiAction(dispatch, EDIT_USER, [
          user.id,
          role,
          networkIds,
          disabled,
        ]);
        showSuccess("User edited successfully.");
      }
      onCancel();
    } catch (err) {
      console.error(err);
    }
  }, [dispatch, form, mode, onCancel, user, validate]);

  const commitLabel =
    mode === "invite" ? (ssoEnabled ? "Save" : "Invite") : "Edit";
  const title =
    mode === "invite"
      ? ssoEnabled
        ? "Add User"
        : "Invite User"
      : `Edit ${user?.name ?? ""}`;

  return (
    <Drawer
      commitLabel={commitLabel}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleCommit}
      open={open}
      title={title}
    >
      <Stack direction="column" spacing={2}>
        {ssoEnabled &&
          (mode === "invite" || (mode === "edit" && userIsDisabled)) && (
            <Alert severity="info">
              Your organization uses Vistar logins to access Fleet. Before
              adding a user, ensure their email address is already active in the
              Vistar Platform.
            </Alert>
          )}
        <TextInput
          disabled={mode === "edit"}
          error={Boolean(errors.name)}
          fullWidth
          helperText={errors.name || (mode === "invite" ? "" : undefined)}
          label="Name"
          onChange={(newValue) => {
            setForm((s) => ({ ...s, name: newValue ?? "" }));
            if (errors.name) clearError("name");
          }}
          placeholder="Apollo Creed"
          value={form.name}
        />
        {mode === "invite" && (
          <TextInput
            error={Boolean(errors.email)}
            fullWidth
            helperText={
              errors.email ||
              (ssoEnabled
                ? "Email address must be active in the Vistar Platform"
                : "Invitation will be sent to this email address.")
            }
            label="Email"
            onChange={(newValue) => {
              setForm((s) => ({ ...s, email: newValue ?? "" }));
              if (errors.email) clearError("email");
            }}
            placeholder="user@example.com"
            value={form.email}
          />
        )}
        <Autocomplete
          getOptionKey={(option: Option) => option.value}
          inputProps={{
            error: Boolean(errors.role),
            helperText: errors.role || "Please select the user access level.",
            label: "Access Level",
            placeholder: "Select User Role",
            required: true,
          }}
          onChange={(_e, opt: Option | null) => {
            setForm((s) => ({
              ...s,
              role: opt?.value || "",
            }));
            if (
              mode === "edit" &&
              user &&
              (user.role === ROLE.ADMIN || user.role === ROLE.ADMIN_OPERATOR)
            ) {
              setForm((s) => ({
                ...s,
                networks: null,
              }));
            }
            if (errors.role) {
              clearError("role");
              clearError("networks");
            }
          }}
          options={USER_ROLES.filter((r) =>
            r.views.has(
              mode === "invite" ? FormAction.CREATE : FormAction.EDIT,
            ),
          )}
          renderOption={(props, option) => (
            <ListItem {...props}>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                width="100%"
              >
                <Box component="span">{option.label}</Box>
                <Typography variant="caption">{option.detail}</Typography>
              </Stack>
            </ListItem>
          )}
          size="small"
          value={
            USER_ROLES.find((option) => option.value === form.role) || null
          }
        />
        {enableNetworkSelection && (
          <Autocomplete
            getOptionKey={(option: Option) => option.value}
            inputProps={{
              error: Boolean(errors.networks),
              helperText:
                errors.networks ||
                "Select at least one network for this user to manage.",
              label: "Target networks",
              required: true,
            }}
            multiple
            onChange={(_e, opt: Option[] | null) => {
              setForm((s) => ({
                ...s,
                networks: opt ? opt.map((o) => Number(o.value)) : [],
              }));
              if (errors.networks) clearError("networks");
            }}
            options={networkOptions}
            size="small"
            value={
              networkOptions
                .filter((o) => form.networks?.includes(Number(o.value)))
                .slice() || []
            }
          />
        )}
        {mode === "edit" && (
          <ToggleSection
            checked={form.disabled ?? false}
            description="Disables the user from accessing Fleet."
            onChange={(event) =>
              setForm((prev) => ({ ...prev, disabled: event.target.checked }))
            }
            title="Disable user"
          ></ToggleSection>
        )}
      </Stack>
    </Drawer>
  );
}
